import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AddSquare, ReceiptItem } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { Link } from "@/components/Atoms";
import { BillingShopRow, PageHeader, SefanoButton } from "@/components/Molecules";
import {
  BillingCommissionsList,
  BillingInvoicesList,
  BillingStatsRow,
} from "@/components/Organisms";
import { PageTemplate } from "@/components/Templates/Page";
import type { BillingTemplateProps } from "./Billing.template.types";
import * as styles from "./Billing.template.styles";

export type { BillingTemplateProps } from "./Billing.template.types";

export function BillingTemplate({
  summary,
  shops,
  commissions,
  invoices,
  rateLabel,
  debtLimitLabel,
  debtLimit,
  isLoading,
  isSummaryLoading,
  isEmpty,
  isPayPending,
  payingStoreId,
  onPayDebt,
  canPayDebt,
}: BillingTemplateProps) {
  const { t } = useTranslation("billing");
  const { t: tCommission } = useTranslation("commission");
  const theme = useTheme();

  if (isLoading) {
    return (
      <PageTemplate maxWidth={1100}>
        <Box sx={styles.desktopHeader}>
          <PageHeader title={t("title")} subtitle={t("subtitle", { rate: rateLabel })} />
        </Box>
        <Box sx={styles.mobileHeader}>
          <Typography component="h1" sx={styles.mobileTitle}>
            {t("mobileTitle")}
          </Typography>
          <Typography component="p" sx={styles.mobileSubtitle}>
            {t("mobileSubtitle", { rate: rateLabel })}
          </Typography>
        </Box>
        <Box sx={styles.loadingWrap}>
          <CircularProgress />
        </Box>
      </PageTemplate>
    );
  }

  const shopRows = shops.map((shop) => (
    <BillingShopRow
      key={shop.store_id}
      shop={shop}
      debtLimit={debtLimit}
      canPayDebt={canPayDebt}
      isPayPending={isPayPending}
      isPaying={payingStoreId === shop.store_id}
      onPayDebt={onPayDebt}
    />
  ));

  return (
    <PageTemplate maxWidth={1100}>
      <Box sx={styles.pageStack}>
        <Box sx={styles.desktopHeader}>
          <PageHeader title={t("title")} subtitle={t("subtitle", { rate: rateLabel })} />
        </Box>

        <Box sx={styles.mobileHeader}>
          <Typography component="h1" sx={styles.mobileTitle}>
            {t("mobileTitle")}
          </Typography>
          <Typography component="p" sx={styles.mobileSubtitle}>
            {t("mobileSubtitle", { rate: rateLabel })}
          </Typography>
        </Box>

        {isEmpty ? (
          <>
            <Box sx={styles.desktopEmptyStateCard}>
              <Box sx={styles.desktopEmptyIconWrap}>
                <ReceiptItem size={58} variant="Bold" color={theme.palette.primary.main} />
              </Box>
              <Typography sx={styles.desktopEmptyTitle}>{t("emptyTitle")}</Typography>
              <Typography sx={styles.desktopEmptyDescription}>{t("emptyDescription")}</Typography>
              <Box sx={styles.emptyActionWrap}>
                <Link to="/shops/create">
                  <SefanoButton intent="add">{t("emptyAction")}</SefanoButton>
                </Link>
              </Box>
            </Box>

            <Box sx={styles.mobileEmptyState}>
              <Box sx={styles.mobileEmptyIconWrap}>
                <ReceiptItem size={52} variant="Bold" color={theme.palette.primary.main} />
              </Box>
              <Typography sx={styles.mobileEmptyTitle}>{t("emptyTitle")}</Typography>
              <Typography sx={styles.mobileEmptyDescription}>{t("mobileEmptyDescription")}</Typography>
              <Link to="/shops/create">
                <SefanoButton
                  intent="add"
                  startIcon={
                    <AddSquare size={19} variant="Linear" color={theme.palette.primary.contrastText} />
                  }
                  sx={styles.mobileEmptyButton}
                >
                  {t("emptyAction")}
                </SefanoButton>
              </Link>
            </Box>
          </>
        ) : (
          <Box sx={styles.populatedContent}>
            <BillingStatsRow summary={summary} isLoading={isSummaryLoading} />
            <Box sx={styles.infoNote}>
              <Typography sx={styles.howItWorksTitle}>{t("howItWorks.title")}</Typography>
              <Typography sx={styles.howItWorksLine}>{t("howItWorks.online")}</Typography>
              <Typography sx={styles.howItWorksLine}>{t("howItWorks.offline")}</Typography>
              <Typography sx={styles.howItWorksLine}>
                {t("howItWorks.limit", { limit: debtLimitLabel })}
              </Typography>
            </Box>
            <Box sx={styles.shopList}>{shopRows}</Box>
            {canPayDebt ? null : (
              <Typography sx={styles.infoNote}>{tCommission("contactOwner")}</Typography>
            )}
            <BillingCommissionsList commissions={commissions} />
            <BillingInvoicesList invoices={invoices} />
          </Box>
        )}
      </Box>
    </PageTemplate>
  );
}
